import React, { useState, useRef } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Camera, Upload, Check, AlertCircle, LogOut, Inbox, Monitor, FolderOpen } from 'lucide-react';

/**
 * Mobil "simple"-modus: Skann/last opp kvittering rett til innboks.
 * Brukes typisk av medarbeider i felten – regnskapsfører behandler i full-versjon.
 */
const Skann = () => {
  const { user, logout, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // {type:'ok'|'feil', tekst}
  const [forhandsvisning, setForhandsvisning] = useState(null);
  const [notat, setNotat] = useState('');
  const [siste, setSiste] = useState([]);
  const fileRef = useRef(null);
  const galleryRef = useRef(null);

  const lastSiste = () => {
    api.get('/innboks', { params: { status: 'alle' } })
      .then((r) => setSiste(r.data.slice(0, 8)));
  };
  React.useEffect(() => { lastSiste(); }, []);

  const valgteFil = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setForhandsvisning({ fil: f, url: URL.createObjectURL(f) });
    setStatus(null);
  };

  const sendInn = async () => {
    if (!forhandsvisning) return;
    setBusy(true); setStatus(null);
    try {
      const fd = new FormData();
      fd.append('fil', forhandsvisning.fil);
      if (notat) fd.append('notat', notat);
      const r = await api.post('/innboks', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      URL.revokeObjectURL(forhandsvisning.url);
      setForhandsvisning(null); setNotat('');
      if (r.data?.ocr_feil) {
        setStatus({ type: 'ok', tekst: `Sendt ✓ (OCR feilet: ${r.data.ocr_feil} – fyll inn manuelt på Innboks)` });
      } else if (r.data?.ocr_data) {
        setStatus({ type: 'ok', tekst: 'Sendt + OCR fullført ✓' });
      } else {
        setStatus({ type: 'ok', tekst: 'Sendt til innboks ✓' });
      }
      lastSiste();
    } catch (e) {
      setStatus({ type: 'feil', tekst: e.response?.data?.error || 'Feil ved opplasting.' });
    } finally { setBusy(false); }
  };

  const byttTilFull = async () => {
    await api.post('/auth/visning', { visning_modus: 'full' });
    await refresh();
    window.location.href = '/';
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--theme-bg)', color: 'var(--theme-text)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <header style={{
        padding: '12px 16px', borderBottom: '1px solid var(--theme-border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: 'var(--theme-bg-card)',
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Skann kvittering</div>
          <div className="fm-text-muted" style={{ fontSize: 11 }}>{user?.epost}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {user?.rolle !== 'ansatt' && (
            <button
              onClick={byttTilFull}
              title="Bytt til full versjon"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 12px', borderRadius: 8,
                background: 'var(--theme-accent)', color: '#fff',
                border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 600,
              }}>
              <Monitor size={16} />
              <span>Full versjon</span>
            </button>
          )}
          <button
            onClick={logout}
            title="Logg ut"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 12px', borderRadius: 8,
              background: 'transparent', color: 'var(--theme-danger)',
              border: '1px solid var(--theme-danger)', cursor: 'pointer',
              fontSize: 13, fontWeight: 600,
            }}>
            <LogOut size={16} />
            <span>Logg ut</span>
          </button>
        </div>
      </header>

      {/* Hovedinnhold */}
      <div style={{ flex: 1, padding: 16, maxWidth: 480, margin: '0 auto', width: '100%' }}>
        {!forhandsvisning && (
          <>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              style={{
                width: '100%', minHeight: 180, padding: '24px 16px',
                border: '2px dashed var(--theme-border)',
                borderRadius: 12, background: 'transparent',
                color: 'var(--theme-text)', cursor: 'pointer',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 10,
              }}
            >
              <Camera size={42} style={{ color: 'var(--theme-accent)' }} />
              <div style={{ fontSize: 17, fontWeight: 600 }}>Ta bilde av kvittering</div>
              <div className="fm-text-muted" style={{ fontSize: 12 }}>
                Åpner kameraet
              </div>
            </button>
            <button
              onClick={() => galleryRef.current?.click()}
              disabled={busy}
              style={{
                marginTop: 10, width: '100%', minHeight: 80, padding: '16px',
                border: '2px dashed var(--theme-border)',
                borderRadius: 12, background: 'transparent',
                color: 'var(--theme-text)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              }}
            >
              <FolderOpen size={24} style={{ color: 'var(--theme-accent)' }} />
              <div style={{ fontSize: 14, fontWeight: 600 }}>Velg fra galleri / fil</div>
            </button>
            <input
              ref={fileRef} type="file"
              accept="image/*" capture="environment"
              onChange={valgteFil}
              style={{ display: 'none' }}
            />
            <input
              ref={galleryRef} type="file"
              accept="image/*,application/pdf"
              onChange={valgteFil}
              style={{ display: 'none' }}
            />
          </>
        )}

        {forhandsvisning && (
          <div>
            <img src={forhandsvisning.url} alt="forhåndsvisning"
                 style={{ width: '100%', borderRadius: 8, marginBottom: 12,
                          maxHeight: '60vh', objectFit: 'contain',
                          background: '#000' }} />
            <div style={{ marginBottom: 10 }}>
              <label>Notat (valgfritt)</label>
              <input value={notat} onChange={(e) => setNotat(e.target.value)}
                     placeholder="F.eks. lunsj med kunde" />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-ghost" style={{ flex: 1 }}
                      onClick={() => { URL.revokeObjectURL(forhandsvisning.url);
                                       setForhandsvisning(null); }}>
                Avbryt
              </button>
              <button className="btn-primary" style={{ flex: 2 }}
                      disabled={busy} onClick={sendInn}>
                <Upload size={14} /> {busy ? 'Sender…' : 'Send til innboks'}
              </button>
            </div>
          </div>
        )}

        {status && (
          <div style={{
            marginTop: 16, padding: 12, borderRadius: 8,
            background: status.type === 'ok'
              ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
            color: status.type === 'ok' ? 'var(--theme-success)' : 'var(--theme-danger)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            {status.type === 'ok' ? <Check size={16} /> : <AlertCircle size={16} />}
            {status.tekst}
          </div>
        )}

        {siste.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h3 style={{ fontSize: 13, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Inbox size={14} /> Sist sendt
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {siste.map((s) => (
                <div key={s.id} style={{
                  padding: 10, borderRadius: 6,
                  background: 'var(--theme-bg-card)',
                  border: '1px solid var(--theme-border)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: 12,
                }}>
                  <div>
                    <div>{s.filnavn || `Skann #${s.id}`}</div>
                    <div className="fm-text-muted" style={{ fontSize: 10 }}>
                      {s.opplastet?.slice(0, 16).replace('T', ' ')}
                    </div>
                  </div>
                  <span className={`badge ${s.status === 'behandlet' ? 'badge-success' :
                                            s.status === 'forkastet' ? 'badge-danger' : 'badge-neutral'}`}>
                    {s.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Skann;
